<template>
  <div
    id="media-sign"
  >
    <div
      class="fixed-full overflow-hidden"
      @click="handleClick"
    >
      <warning-message
        v-if="!site.loaded || loading"
        is-loading-message
      >
        {{ $t('status.loading') }}
      </warning-message>
      <warning-message
        v-else-if="currentDataArray.length === 0 && videoIndex === null"
      >
        {{ $t('showing.noShowingsToday') }}
      </warning-message>
      <video
        v-else-if="currentSourceType === MEDIA_FORMAT.VIDEO"
        ref="videoWindow"
        :style="`background-color: ${mediaBackgroundColor}; height: ${videoDisplayHeight};`"
        autoplay
        :muted="muted"
        @ended="nextVideo()"
        @error="nextVideo()"
      >
        <source
          :src="currentDataSourceUrl"
          type="video/mp4"
        >
        Your browser does not support embedded videos!
      </video>
      <img
        v-else
        :style="`height: ${imgDisplayHeight}; background-color: ${mediaBackgroundColor};`"
        :src="currentDataSourceUrl + '?fit=fit&w=' + $q.screen.width + '&h=' + $q.screen.height"
      >
      <div
        v-if="(displayMessages && !!currentFooterText) || (displayButton && !!currentButtonText)"
        class="media-footer-wrapper"

        :style="{
          width: $kiosk ? '100%' : '100vw',
          'background-color': messageBackgroundColor ? `${messageBackgroundColor}` : `#000000`,
          'bottom': displayFooter ? '8vh' : '0'
        }"
      >
        <div
          class="media-footer row justify-between items-center full-height q-px-md"
        >
          <div
            v-if="displayMessages && !!currentFooterText"
            class="text-uppercase text-bold col"
            :class="{ 'text-left': displayButton && !!currentButtonText }"
            :style="{
              color: messageAndButtonColor,
              'font-size': $signage ? '4vh' : '2vh'
            }"
          >
            {{ currentFooterText }} {{ currentUrlLink }}
          </div>
          <q-btn
            v-if="displayButton && !!currentButtonText"
            :class="displayMessages && !!currentFooterText ? 'col-auto': 'q-ml-auto'"
            outline
            :style="{
              color: messageAndButtonColor,
              height: $isLandscapeView() ? undefined : '50px'
            }"
            style="width: auto;"
          >
            {{ currentButtonText }}
          </q-btn>
        </div>
      </div>

      <div
        v-if="displayFooter"
        :style="`background-color: ${footerBackgroundColor}`"
        class="footer"
      >
        <img
          class="site-logo"
          :style="`background-color: ${footerBackgroundColor}`"
          :src="site.lightLogoImageUrl"
        >
        <div
          v-if="displayTime"
          class="time text-uppercase"
        >
          {{ site.siteTime.format("h:mm a") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { colors } from 'quasar'
import { imgixHost, region } from 'src/config'
import { MEDIA_FORMAT } from 'src/models/signage/SignageMediaItem'
import WarningMessage from 'src/modules/signage/components/WarningMessage'
import { inject } from 'vue'

const { lighten } = colors

const DATA_SOURCE_OPTIONS = {
  MOVIE: 'movie',
  MEDIA: 'media',
  MOVIE_IMAGES: 'movie-images',
  MOVIE_ANIMATED_POSTERS: 'movie-animated-posters'
}

export default {
  name: 'Media',
  components: {
    WarningMessage
  },
  props: {
    isComponent: {
      type: Boolean,
      required: false,
      default: false
    },
    siteId: {
      type: Number,
      required: false,
      default () {
        const route = inject('route')
        return route?.params?.siteId
      }
    },
    displayTrailers: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-trailers'] !== 'false'
      }
    },
    displayMedia: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-media'] !== 'false'
      }
    },
    titleClassId: {
      type: Number,
      required: false,
      default () {
        const route = inject('route')
        return parseInt(route?.query && route.query['title-class-id'])
      }
    },
    mediaClassId: {
      type: Number,
      required: false,
      default () {
        const route = inject('route')
        return parseInt(route?.query && parseInt(route.query['media-class-id']))
      }
    },
    muted: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query?.muted !== 'false'
      }
    },
    displayFooter: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-footer'] === 'true'
      }
    },
    displayMessages: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-messages'] === 'true'
      }
    },
    displayAnimatedPosters: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-animated-posters'] === 'true'
      }
    },
    displayMovieBanners: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-movie-banners'] === 'true'
      }
    },
    displayMoviePosters: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-movie-posters'] === 'true'
      }
    },
    footerBackgroundColor: {
      type: String,
      required: false,
      default () {
        const route = inject('route')
        return (route?.query && route.query['footer-background-color']) || '#FFFFFF'
      }
    },
    mediaBackgroundColor: {
      type: String,
      required: false,
      default () {
        const route = inject('route')
        return (route?.query && route.query['media-background-color']) || '#000000'
      }
    },
    messageBackgroundColor: {
      type: String,
      required: false,
      default () {
        const route = inject('route')
        return (route?.query && route.query['message-background-color']) || '#AAAAAA'
      }
    },
    defaultDisplayDuration: {
      type: Number,
      required: false,
      default () {
        const route = inject('route')
        return (route?.query && parseInt(route.query['default-display-duration'])) || 5
      }
    },
    displayTime: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-time'] !== 'false'
      }
    },
    footerTextColor: {
      type: String,
      required: false,
      default () {
        const route = inject('route')
        return (route?.query && route.query['footer-text-color']) || '#191C5E'
      }
    },
    imgHeight: {
      type: Number,
      required: false,
      default: 100
    },
    hasLink: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['has-link'] === 'true'
      }
    },
    displayButton: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['display-button'] === 'true'
      }
    }
  },
  models: {
    site: {
      fields: ['id', 'name', 'primaryColor', 'lightLogoImage', 'migratedTo'],
      enabledSignageMediaMovies: {
        allDatesWithPublicShowing: true
      },
      enabledSignageMediaItems: {
        signageMediaClass: 'default'
      },
      variables () {
        return {
          id: this.siteId
        }
      },
      immediate: true
    }
  },
  data () {
    return {
      videoSource: null,
      videoIndex: null,
      updateKey: 0,
      currentData: DATA_SOURCE_OPTIONS.MEDIA,
      blobData: null,
      loading: true,
      currentDataSourceUrl: null,
      currentKeys: [],
      destroyed: false,
      nextVideoTimeout: null,
      previousBlobUrl: null
    }
  },
  configuration: ['currentSite'],
  computed: {
    movies () {
      if (!this.site.enabledSignageMediaMovies || !this.displayTrailers) return []
      return _.filter(this.site.enabledSignageMediaMovies.data, movie => !!movie.trailerVideoUrl && movie.signageDisplayTrailer && (!this.titleClassId || movie.titleClassId === this.titleClassId))
    },
    movieAnimatedPosters () {
      if (!this.site.enabledSignageMediaMovies || !this.displayAnimatedPosters) return []
      return _.filter(this.site.enabledSignageMediaMovies.data, movie => !!movie.animatedPosterVideoUrl && movie.signageDisplayAnimatedPoster && (!this.titleClassId || movie.titleClassId === this.titleClassId))
    },
    movieImages () {
      if (!this.site.enabledSignageMediaMovies || (!this.displayMovieBanners && !this.displayMoviePosters)) return []
      const images = []
      const filteredMovies = _.filter(this.site.enabledSignageMediaMovies.data, movie => (!!movie.posterImage || !!movie.bannerImage) && (!this.titleClassId || movie.titleClassId === this.titleClassId))
      _.each(filteredMovies, movie => {
        if (movie.posterImage && movie.signageDisplayPoster && this.displayMoviePosters) images.push({ movie, image: `${imgixHost}/${movie.posterImage}?auto=format&cs=origin` })
        if (movie.bannerImage && movie.signageDisplayBanner && this.displayMovieBanners) images.push({ movie, image: `${imgixHost}/${movie.bannerImage}?auto=format&cs=origin` })
      })
      return images
    },
    mediaItems () {
      if (!this.site.enabledSignageMediaItems || !this.displayMedia) return []
      return _.filter(this.site.enabledSignageMediaItems.data, mediaItem => !this.mediaClassId || mediaItem.signageMediaClassId === this.mediaClassId)
    },
    currentDataArray () {
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA) {
        return this.mediaItems
      } else if (this.currentData === DATA_SOURCE_OPTIONS.MOVIE) {
        return this.movies
      } else if (this.currentData === DATA_SOURCE_OPTIONS.MOVIE_ANIMATED_POSTERS) {
        return this.movieAnimatedPosters
      } else {
        return this.movieImages
      }
    },
    currentUrl () {
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA ||
      this.currentData === DATA_SOURCE_OPTIONS.MOVIE ||
      this.currentData === DATA_SOURCE_OPTIONS.MOVIE_ANIMATED_POSTERS) {
        const resolvedItem = (this.currentDataArray[this.videoIndex]?.media ||
        (this.currentData === DATA_SOURCE_OPTIONS.MOVIE &&
        this.currentDataArray[this.videoIndex]?.trailerVideo) ||
        this.currentDataArray[this.videoIndex]?.animatedPosterVideo)

        // Check if we currently have this key in our indexed db
        if (_.includes(this.currentKeys, resolvedItem)) {
          // Key exists in indexed DB, return blob.
          return this.$localDb.get('media', resolvedItem)
        }

        // Key doesn't exist, return standard URL
        if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA) return this.currentDataArray[this.videoIndex]?.mediaUrl
        if (this.currentData === DATA_SOURCE_OPTIONS.MOVIE) return this.currentDataArray[this.videoIndex]?.trailerVideoUrl
        return this.currentDataArray[this.videoIndex]?.animatedPosterVideoUrl
      } else if (this.currentData === DATA_SOURCE_OPTIONS.MOVIE_IMAGES) {
        return this.currentDataArray[this.videoIndex]?.image
      } else {
        return null
      }
    },
    currentSourceType () {
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA) {
        return this.currentDataArray[this.videoIndex]?.mediaFormat
      }
      if (this.currentData === DATA_SOURCE_OPTIONS.MOVIE || this.currentData === DATA_SOURCE_OPTIONS.MOVIE_ANIMATED_POSTERS) {
        return MEDIA_FORMAT.VIDEO
      }
      return MEDIA_FORMAT.IMAGE
    },
    currentFooterText () {
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA) {
        return this.currentDataArray[this.videoIndex]?.footerText
      } else {
        if (this.currentDataArray[this.videoIndex]?.movie?.signageMessageOverride) {
          return this.currentDataArray[this.videoIndex]?.movie?.signageMessageOverride
        } else {
          return this.currentDataArray[this.videoIndex]?.movie?.hasPlayedOrPlayingToday ? this.$i18n.t('movie.nowPlaying') : this.$i18n.t('movie.comingSoon')
        }
      }
    },
    currentButtonText () {
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA && this.currentDataArray[this.videoIndex]?.hasLink) {
        return this.currentDataArray[this.videoIndex]?.buttonText
      } else {
        return null
      }
    },
    heightIdentifier () {
      return this.isComponent ? '%' : 'vh'
    },
    videoDisplayHeight () {
      let height = 100

      if (this.displayFooter) {
        height -= 8
      }

      if (this.displayMessages && this.currentFooterText) {
        height -= 5.5
      }

      return height + this.heightIdentifier
    },
    imgDisplayHeight () {
      let height = this.imgHeight

      if (this.displayFooter) {
        height -= 8
      }

      if (this.displayMessages && this.currentFooterText) {
        height -= 5.5
      }

      return height + this.heightIdentifier
    },
    messageAndButtonColor () {
      if (this.footerTextColor) {
        return this.footerTextColor
      } else {
        return this.$isColorDark(this.messageBackgroundColor) || !this.messageBackgroundColor ? 'white' : 'black'
      }
    }
  },
  watch: {
    updateKey () {
      if (this.videoIndex === null) return
      if (this.dataItemFormat(this.currentDataArray[this.videoIndex]) === MEDIA_FORMAT.IMAGE) {
        this.nextVideo((this.currentDataArray[this.videoIndex]?.displayDuration || this.defaultDisplayDuration) * 1000)
      } else {
        this.$refs?.videoWindow?.load()
      }
    },
    async currentUrl () {
      // Handle case where this is object has not loaded
      if (!this.site.enabledSignageMediaItems) return

      const previousDataSourceUrl = this.currentDataSourceUrl
      await Promise.resolve(this.currentUrl).then(result => {
        if (!result) {
          // Could not resolve the URL, skip this.
          this.previousBlobUrl = null
          this.nextVideo()
        } else if (this.previousBlobUrl && this.previousBlobUrl === result?.id) {
          // This check handles site fetching interval and prevents restarting the video
          // Instead we just ensure it is playing
          this.$refs?.videoWindow?.play()
          return
        } else if (result?.data) {
          // This is a resonse in the form of a blob object
          this.previousBlobUrl = result?.id
          const blobUrl = window.URL.createObjectURL(result.data)
          this.currentDataSourceUrl = blobUrl
          ++this.updateKey
        } else {
          // This is a imgix URL, not from indexed DB
          this.previousBlobUrl = null
          this.currentDataSourceUrl = result
        }
        // Revoke Previous Blob URL tp prevent memory leaks
        URL.revokeObjectURL(previousDataSourceUrl)
      }).catch(async exception => {
        this.$rollbar.error(`Failed to resolve computed url: ${this.currentDataSourceUrl}`, exception)
        // Video BLOB exists in current keys but not in indexed db.
        // Refreshing current keys will trigger recompute of current URL.
        this.currentKeys = await this.$localDb.getAllKeys('media')
        URL.revokeObjectURL(previousDataSourceUrl)
        this.nextVideo()
      })
    }
  },
  created () {
    _.extend(this, { MEDIA_FORMAT, DATA_SOURCE_OPTIONS })
    this.lighten = lighten
  },
  async mounted () {
    this.destroyed = false
    window.mediaDebugger = this
    this.mediaIntervalId = setInterval(async () => {
      await this.site.fetch(true)
      this.addVideos()
    }, 10 * 60 * 1000)

    await this.site.ensureLoaded()

    if (this.site.migratedTo && this.site.migratedTo !== region) {
      let newUrl = ''
      if (region === 'us' && !window.location.href.includes('-us.')) {
        newUrl = window.location.href.replace('.indy.systems', `${this.site.migratedTo}.indy.systems`)
      } else {
        newUrl = window.location.href.replace(region, this.site.migratedTo)
      }

      if (newUrl !== window.location.href) {
        window.location = newUrl
      }
    }

    if (this.$signage) {
      // Fetching the site when in the app mode of digital-signage, sets the site_id cookie
      // This will reload the configuration data after being authenticated
      this.$handleAuthChange()
    }
    this.addVideos()

    this.nextVideo()
  },
  beforeUnmount () {
    this.destroyed = true
    if (this.mediaIntervalId) clearInterval(this.mediaIntervalId)
  },
  methods: {
    nextVideo (ms = null) {
      if (this.destroyed) return

      // We only want this method to be executed once.
      // Letting this method handle the timeouts ensures we don't unintentionally skip media if called twice.
      clearTimeout(this.nextVideoTimeout)

      if (ms) {
        this.nextVideoTimeout = setTimeout(() => { this.nextVideo() }, ms)
        return
      }

      if (this.movies.length === 0 && this.mediaItems.length === 0 && this.movieImages.length === 0 && this.movieAnimatedPosters.length === 0) {
        // This is reccursive and handles no data. Once data exists we break this loop.
        this.nextVideo(1000)
        return
      } else if (this.videoIndex === null || this.videoIndex + 1 >= this.currentDataArray.length) {
        // We have no media left for the current media type, move on to next one.
        if (!this.nextDataSource()) {
          return
        }
      } else {
        this.videoIndex++
      }
      this.updateKey += 1
      return true
    },
    nextDataSource () {
      this.videoIndex = 0

      const dataOptions = []

      if (this.movies.length !== 0) dataOptions.push(DATA_SOURCE_OPTIONS.MOVIE)
      if (this.movieImages.length !== 0) dataOptions.push(DATA_SOURCE_OPTIONS.MOVIE_IMAGES)
      if (this.mediaItems.length !== 0) dataOptions.push(DATA_SOURCE_OPTIONS.MEDIA)
      if (this.movieAnimatedPosters.length !== 0) dataOptions.push(DATA_SOURCE_OPTIONS.MOVIE_ANIMATED_POSTERS)

      if (dataOptions.length === 0) {
        this.nextVideo(1000)
        return false
      }

      const dataPlace = dataOptions.indexOf(this.currentData)

      if (dataPlace + 1 >= dataOptions.length) {
        this.currentData = dataOptions[0]
      } else {
        this.currentData = dataOptions[dataPlace + 1]
      }

      return true
    },
    async addVideos () {
      await this.site.ensureLoaded()

      const promises = []
      let validKeys = []

      if (this.displayTrailers) {
        validKeys = [...validKeys, ..._.map(this.movies, 'trailerVideo')]
      }

      if (this.displayAnimatedPosters) {
        validKeys = [...validKeys, ..._.map(this.movieAnimatedPosters, 'animatedPosterVideo')]
      }

      if (this.displayMedia) {
        validKeys = [...validKeys, ..._.map(_.filter(this.mediaItems, { mediaFormat: 'video' }), 'media')]
      }
      const existingKeys = await this.$localDb.getAllKeys('media')
      const keysToRemove = _.difference(existingKeys, validKeys)
      _.each(keysToRemove, key => this.$localDb.delete('media', key))
      _.each(this.movies, movie => {
        if (!existingKeys.find(element => element === movie.trailerVideo)) {
          promises.push(this.fetchVideoFromNetwork(movie.trailerVideo, movie.trailerVideoUrl))
        }
      })
      _.each(this.movieAnimatedPosters, movie => {
        if (!existingKeys.find(element => element === movie.animatedPosterVideo)) {
          promises.push(this.fetchVideoFromNetwork(movie.animatedPosterVideo, movie.animatedPosterVideoUrl))
        }
      })
      _.each(_.filter(this.mediaItems, mi => mi.mediaFormat === MEDIA_FORMAT.VIDEO), mediaItem => {
        if (!existingKeys.find(element => element === mediaItem.media)) {
          promises.push(this.fetchVideoFromNetwork(mediaItem.media, mediaItem.mediaUrl))
        }
      })

      this.currentKeys = await this.$localDb.getAllKeys('media')
      this.loading = false

      await Promise.all(promises).then(async () => {
        this.currentKeys = await this.$localDb.getAllKeys('media')
      })
    },
    async fetchVideoFromNetwork (id, url) {
      const mediaBlob = fetch(url).then(response => response.blob())

      return Promise.all([mediaBlob]).then(values => {
        this.$localDb.store('media', id, values[0])
      })
    },
    dataItemFormat (item) {
      return item?.mediaFormat || item?.animatedPosterVideo || item?.trailerVideo || MEDIA_FORMAT.IMAGE
    },
    handleClick () {
      const mediaItem = this.currentDataArray[this.videoIndex]
      if (this.currentData === DATA_SOURCE_OPTIONS.MEDIA && mediaItem?.hasLink && mediaItem?.movieId) {
        this.$router.push(this.currentSite.path(`/movie/${mediaItem.movieId}`))
      }
    }
  }
}
</script>

<style  lang="scss" scoped>
video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

img{
  display:block;
  width:100%;
  object-fit: scale-down;
  position: absolute;
}

.message {
  margin-top: 300px;
  font-size: 64px;
  text-align: center;
}

.site-logo {
  max-height: 7.5vh;
  max-width: 50vw;
  padding-top: 0.5vh;
}

.time {
  position: absolute;
  right: 2vh;
  font-size: 5vh;
}

.media-footer-wrapper {
  text-align: center;
  height: 5.5vh;

  position: absolute;
}

.media-footer {
  text-align: center;
}

.footer {
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 0;
  height: 8vh;
  width: 100vw;
}

</style>
